// components
import { Tooltip, Typography } from '@mui/material';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Icon, IconButton } from '@sdb/planning-ui';

// hooks
import { useEffect, useState } from 'react';
import { useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';

// types
import { Entry } from 'RestAPIs/quality-monitor';
import { OpenRequestsProps, SlicedTableLoading } from './OpenRequests.types';

// styles
import { StyledRowStackAlignItemsSmallGap } from 'components/shared/Stack/Stack.styles';
import { StyledTitleTypography } from './TasksList.styles';
import { StyledPaper } from 'components/shared/Paper/StyledPaper.styles';
import {
  StyledDateTypography,
  StyledOpenRequestsStack,
  StyledOpenRequestsTableBox,
  StyledOpenRequestsTableCell,
  StyledOpenRequestsTableContainer
} from './OpenRequests.styles';

// utils
import axios from 'axios';
import classNames from 'classnames';
import { format, isSaturday, isSunday, isToday } from 'date-fns';
import dayjs from 'dayjs';
import { noop } from 'lodash';
import { getPlanunitCommaIds } from 'components/utils';
import { formatByLocale } from 'utils/date';
import { getData } from 'RestAPIs/quality-monitor';
import { countTotalActions, formatDatePair } from './OpenRequests.utils';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);

const SlicedTable = ({ data, start, end, openRoster }: any) => {
  const [loading, setLoading] = useState<SlicedTableLoading | null>(null);
  const onOpenRoster = (id, date?) => {
    openRoster(id, date, true);
    setLoading({ id, date });
  };

  return (
    <StyledOpenRequestsTableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {data[0].days.slice(start, end).map(({ date }) => (
              <StyledOpenRequestsTableCell
                key={date}
                align="center"
                className={classNames({
                  'is-today': isToday(date),
                  'is-saturday': isSaturday(date),
                  'is-sunday': isSunday(date)
                })}
              >
                <div>
                  <b>{formatByLocale(date, 'EEEEEE')}</b>
                </div>
                <div>{format(date, 'dd')}</div>
              </StyledOpenRequestsTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map(({ costCenterId, days }) => (
            <TableRow key={costCenterId}>
              {days.slice(start, end).map(({ date, totalNumberOfActions }) => (
                <TableCell
                  key={date}
                  align="center"
                  className={classNames({
                    'is-today': isToday(date),
                    'is-saturday': isSaturday(date),
                    'is-sunday': isSunday(date)
                  })}
                >
                  {totalNumberOfActions > 0 ? (
                    <Chip
                      variant="outlined"
                      onClick={() => onOpenRoster(costCenterId, date)}
                      label={
                        loading &&
                        loading.id === costCenterId &&
                        loading.date === date ? (
                          <i className="icon-spinner icon-spin" />
                        ) : (
                          totalNumberOfActions
                        )
                      }
                    />
                  ) : (
                    '-'
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </StyledOpenRequestsTableContainer>
  );
};

export default function OpenRequests({
  setIsLoading,
  selectedPlanUnits,
  setAmountOfOpenDuties
}: OpenRequestsProps) {
  const { t } = useTranslation('common');
  const [data, setData] = useState<Entry[]>([]);
  const [loadingPlanUnitID, setLoadingPlanUnitID] = useState(0);
  const planUnits = useSelector((state) => state.planUnits);
  const startOfThisWeek = dayjs().startOf('week').toDate();
  const endOfThisWeek = dayjs().endOf('week').add(1, 'day').toDate();
  const startOfThisWorkweek = dayjs().startOf('week').toDate();
  const endOfThisWorkweek = dayjs().endOf('week').subtract(1, 'day').toDate();
  const [isWorkweek, setIsWorkweek] = useState(false);
  const [dateRange, setDateRange] = useState({
    startOfWeek: startOfThisWeek,
    endOfWeek: endOfThisWeek
  });

  const { short: formattedStartDate, full: formattedStartDateFull } =
    formatDatePair(dateRange?.startOfWeek);

  const { short: formattedEndDate, full: formattedEndDateFull } =
    formatDatePair(dateRange?.endOfWeek, 1);

  const weekNumber = dayjs(dateRange?.startOfWeek).week();

  const isThisWeek =
    dayjs(startOfThisWeek).toISOString() ===
    dayjs(dateRange.startOfWeek).toISOString();

  useEffect(() => {
    const { CancelToken } = axios;
    const source = CancelToken.source();

    if (selectedPlanUnits.length) {
      const costCenterIds = getPlanunitCommaIds(selectedPlanUnits);
      if (!costCenterIds) {
        setData([]);
        return noop;
      }

      const params = {
        costCenterIds,
        startDate: format(dateRange?.startOfWeek, 'yyyy-MM-dd'),
        endDate: format(dateRange?.endOfWeek, 'yyyy-MM-dd')
      };

      setIsLoading(true);
      getData({ params, cancelToken: source.token }).then(
        ({ data }) => {
          data.forEach(({ days }) =>
            days.forEach((day) => {
              day.date = new Date(day.date);
            })
          );
          setData(data);
          setIsLoading(false);
        },
        (err) => {
          if (!(err && err.message === 'unmount')) {
            setIsLoading(false);
          }
        }
      );
    } else {
      setData([]);
    }

    return () => source.cancel('unmount');
  }, [
    selectedPlanUnits,
    setIsLoading,
    dateRange.startOfWeek,
    dateRange.endOfWeek
  ]);

  useEffect(() => {
    const totalActions = countTotalActions(data);
    setAmountOfOpenDuties(totalActions);
  }, [data]);

  const openRoster = (selectedPlanUnitId, date?, isWeek4View?) => {
    date = date || new Date();
    date.setHours(0, 0, 0, 0);

    window.dojoTopic.publish(
      'setRosterTempPlanunits',
      planUnits.filter(({ id }) => selectedPlanUnitId === id),
      date,
      isWeek4View
    );
    setLoadingPlanUnitID(selectedPlanUnitId);
    // TODO: remove loaders after ROSTER migration is done
  };

  if (!data.length) {
    return <></>;
  }

  const handleWeekOrWorkweekToggle = () => {
    if (isWorkweek) {
      setIsWorkweek(false);
      setDateRange({
        startOfWeek: startOfThisWeek,
        endOfWeek: endOfThisWeek
      });
    } else {
      setIsWorkweek(true);
      setDateRange({
        startOfWeek: startOfThisWorkweek,
        endOfWeek: endOfThisWorkweek
      });
    }
  };

  const handleNextPeriod = (next = false, thisWeek = false) => {
    if (thisWeek) {
      setDateRange({
        startOfWeek: isWorkweek ? startOfThisWorkweek : startOfThisWeek,
        endOfWeek: isWorkweek ? endOfThisWorkweek : endOfThisWeek
      });
    } else if (next) {
      setDateRange({
        startOfWeek: dayjs(dateRange.startOfWeek).add(1, 'week').toDate(),
        endOfWeek: dayjs(dateRange.endOfWeek).add(1, 'week').toDate()
      });
    } else {
      setDateRange({
        startOfWeek: dayjs(dateRange.startOfWeek).subtract(1, 'week').toDate(),
        endOfWeek: dayjs(dateRange.endOfWeek).subtract(1, 'week').toDate()
      });
    }
  };

  return (
    <StyledPaper className="open-requests">
      <StyledOpenRequestsStack>
        <StyledRowStackAlignItemsSmallGap>
          <Icon type="calendar-star" fontSize="medium" />
          <StyledTitleTypography variant="h6">
            {t('openRequests')}
          </StyledTitleTypography>
        </StyledRowStackAlignItemsSmallGap>

        <StyledRowStackAlignItemsSmallGap>
          {!isThisWeek && (
            <Tooltip title={t('thisWeek')} arrow>
              <IconButton
                type="calendar-star"
                onClick={() => handleNextPeriod(false, true)}
              />
            </Tooltip>
          )}
          <Tooltip
            title={
              isWorkweek
                ? t('qualityMonitor.setToWeek')
                : t('qualityMonitor.setToWorkweek')
            }
            arrow
          >
            <IconButton
              type="calendar-clock"
              onClick={handleWeekOrWorkweekToggle}
            />
          </Tooltip>
          <Tooltip title={t('previousWeek')} arrow>
            <IconButton
              type="chevron-left"
              onClick={() => handleNextPeriod(false)}
            />
          </Tooltip>
          <Tooltip
            title={t('qualityMonitor.fromToDate', {
              startDate: formattedStartDateFull,
              endDate: formattedEndDateFull
            })}
            arrow
          >
            <StyledDateTypography variant="body1">
              {t('qualityMonitor.fromToDate', {
                startDate: formattedStartDate,
                endDate: formattedEndDate
              })}
              <Chip label={`Week ${weekNumber}`} size="small" />
            </StyledDateTypography>
          </Tooltip>
          <Tooltip title={t('nextWeek')} arrow>
            <IconButton
              type="chevron-right"
              onClick={() => handleNextPeriod(true)}
            />
          </Tooltip>
        </StyledRowStackAlignItemsSmallGap>
      </StyledOpenRequestsStack>

      <StyledOpenRequestsTableBox>
        <StyledOpenRequestsTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <StyledOpenRequestsTableCell component="th" scope="row">
                  <Typography variant="subtitle1" component="h6">
                    {t('planUnits')}
                  </Typography>
                </StyledOpenRequestsTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map(({ costCenterId, costCenterName }) => (
                <TableRow key={costCenterId}>
                  <TableCell component="th" scope="row">
                    <button
                      type="button"
                      className="plain-btn"
                      onClick={() => openRoster(costCenterId, null, true)}
                    >
                      {costCenterName}
                    </button>
                    {loadingPlanUnitID === costCenterId && (
                      <i className="icon-spinner icon-spin" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledOpenRequestsTableContainer>
        <SlicedTable data={data} start="0" end="1" openRoster={openRoster} />
        <SlicedTable data={data} start="1" end="8" openRoster={openRoster} />
      </StyledOpenRequestsTableBox>
    </StyledPaper>
  );
}
